import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Save,
  X,
  Eye,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Clock,
  BarChart3,
  Calendar,
  User,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import axios from 'axios';

const ModernBookManagement = ({ user }) => {
  const location = useLocation();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  
  // Get search highlight from navigation state
  const highlightBookId = location.state?.highlightBook;
  const searchQuery = location.state?.searchQuery;
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setGenreFilter] = useState('');
  const [categorys, setGenres] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    publisher: '',
    publication_year: '',
    category: '',
    description: '',
    status: 'available',
    pages: '',
    book_copies: 1
  });

  useEffect(() => {
    fetchBooks(true); // Initial load
    fetchGenres();

    // Set search term if coming from search
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, []);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchTerm, statusFilter, categoryFilter]);

  // Fetch books when search/filter/page changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBooks();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, statusFilter, categoryFilter, pagination.page]);

  const fetchBooks = async (isInitialLoad = false) => {
    try {
      // Only show full loading skeleton on initial load
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsSearching(true);
      }
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please login again.');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Build query params including search and filters
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      if (categoryFilter) {
        params.append('category', categoryFilter);
      }

      const response = await axios.get(`/api/books?${params.toString()}`, config);
      
      if (response.data.success) {
        const booksData = response.data.data.books || [];
        setBooks(booksData);
        
        // Update pagination
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total || 0,
          totalPages: response.data.data.pagination.totalPages || 0
        }));
        
      } else {
        setBooks([]);
      }
    } catch (err) {
      console.error('Books fetch error:', err);
      setError('Failed to fetch books. Please check your connection and try again.');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get('/api/books', config);
      if (response.data.success) {
        setGenres(response.data.data.filters.categorys || []);
      }
    } catch (err) {
      console.error('Genres fetch error:', err);
    }
  };

  const handleAddBook = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.post('/api/books', formData, config);
      if (response.data.success) {
        setShowAddForm(false);
        setFormData({
          title: '',
          author: '',
          isbn: '',
          publisher: '',
          publication_year: '',
          category: '',
          description: '',
          status: 'available',
          pages: '',
          book_copies: 1
        });
        fetchBooks();
      } else {
        alert(response.data.message || 'Failed to add book');
      }
    } catch (err) {
      console.error('Add book error:', err);
      alert(err.response?.data?.message || 'Failed to add book');
    }
  };

  const handleEditBook = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.put(`/api/books/${selectedBook.id}`, formData, config);
      if (response.data.success) {
        setShowEditForm(false);
        setSelectedBook(null);
        setMessage({ type: 'success', text: 'Book updated successfully!' });
        fetchBooks();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to update book' });
      }
    } catch (err) {
      console.error('Edit book error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update book' });
    }
  };

  const handleDeleteBook = async (bookId) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.delete(`/api/books/${bookId}`, config);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Book deleted successfully!' });
        setShowDeleteConfirm(null);
        fetchBooks();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to delete book' });
      }
    } catch (err) {
      console.error('Delete book error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to delete book' });
    }
  };

  // Clear message after 3 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Books are already filtered by the server, so use them directly
  const filteredBooks = books;


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
                Book Management
              </h1>
              <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
                Manage your library's book collection and inventory
              </p>
            </div>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Book
            </Button>
          </div>
        </motion.div>


        {/* Search and Filters */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    {isSearching ? (
                      <RefreshCw className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    )}
                    <Input
                      placeholder="Search books..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="available">Available</option>
                    <option value="borrowed">Borrowed</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setGenreFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Genres</option>
                    {categorys.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Books Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Book Collection ({pagination.total || books.length})
              </CardTitle>
              <CardDescription>
                Manage your library's book inventory and availability
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600">{error}</p>
                </div>
              )}
              
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Title</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Author</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Barcode</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Number Code</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-900">Copies</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-900">Available</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Genre</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBooks.map((book, index) => (
                      <motion.tr
                        key={book.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          highlightBookId === book.id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{book.title}</p>
                            <p className="text-sm text-slate-500">ISBN: {book.isbn}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-700">{book.author}</td>
                        <td className="py-4 px-4">
                          <Badge variant="outline" className="font-mono">
                            {book.barcode || 'N/A'}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant="outline" className="font-mono">
                            {book.number_code || 'N/A'}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="font-semibold text-slate-700">
                            {book.book_copies || 0}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge
                            className={
                              book.available_copies > 0
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }
                          >
                            {book.available_copies || 0}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge
                            variant={book.status === 'available' ? 'default' : 'secondary'}
                            className={
                              book.status === 'available'
                                ? 'bg-green-100 text-green-700'
                                : book.status === 'borrowed'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-red-100 text-red-700'
                            }
                          >
                            {book.status === 'available' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {book.status === 'borrowed' && <Clock className="h-3 w-3 mr-1" />}
                            {book.status === 'maintenance' && <AlertCircle className="h-3 w-3 mr-1" />}
                            {book.status.charAt(0).toUpperCase() + book.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant="outline" className="text-xs">
                            <Tag className="h-3 w-3 mr-1" />
                            {book.category}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedBook(book);
                                setFormData(book);
                                setShowEditForm(true);
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(book)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {filteredBooks.map((book, index) => (
                  <motion.div
                    key={book.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 ${
                      highlightBookId === book.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{book.title}</h3>
                        <p className="text-slate-600">by {book.author}</p>
                        <p className="text-sm text-slate-500">ISBN: {book.isbn}</p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Badge 
                          variant={book.status === 'available' ? 'default' : 'secondary'}
                          className={
                            book.status === 'available' 
                              ? 'bg-green-100 text-green-700' 
                              : book.status === 'borrowed'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                          }
                        >
                          {book.status === 'available' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {book.status === 'borrowed' && <Clock className="h-3 w-3 mr-1" />}
                          {book.status === 'maintenance' && <AlertCircle className="h-3 w-3 mr-1" />}
                          {book.status.charAt(0).toUpperCase() + book.status.slice(1)}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <Tag className="h-3 w-3 mr-1" />
                          {book.category}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Barcode</p>
                          <p className="font-mono">{book.barcode || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Number Code</p>
                          <p className="font-mono">{book.number_code || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Total Copies</p>
                          <p className="font-semibold">{book.book_copies || 0}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Available</p>
                          <Badge
                            className={
                              book.available_copies > 0
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }
                          >
                            {book.available_copies || 0}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedBook(book);
                              setFormData(book);
                              setShowEditForm(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDeleteConfirm(book)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {filteredBooks.length === 0 && (
                <div className="text-center py-8">
                  <BookOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No books found</h3>
                  <p className="text-slate-600">
                    {searchTerm ? 'Try adjusting your search terms' : 'No books have been added yet'}
                  </p>
                </div>
              )}

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-slate-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} books
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Add Book Modal */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Add New Book</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddForm(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <form onSubmit={handleAddBook} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Title *
                      </label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                        placeholder="Enter book title"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Author *
                      </label>
                      <Input
                        value={formData.author}
                        onChange={(e) => setFormData({...formData, author: e.target.value})}
                        required
                        placeholder="Enter author name"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        ISBN
                      </label>
                      <Input
                        value={formData.isbn}
                        onChange={(e) => setFormData({...formData, isbn: e.target.value})}
                        placeholder="e.g., 978-0134444321"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Publisher
                      </label>
                      <Input
                        value={formData.publisher}
                        onChange={(e) => setFormData({...formData, publisher: e.target.value})}
                        placeholder="e.g., Pearson Education"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Publication Year
                      </label>
                      <Input
                        type="number"
                        value={formData.publication_year}
                        onChange={(e) => setFormData({...formData, publication_year: e.target.value})}
                        placeholder="e.g., 2023"
                        min="1800"
                        max={new Date().getFullYear()}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Category
                      </label>
                      <Input
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        placeholder="e.g., Programming, Web Development, Database"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Pages
                      </label>
                      <Input
                        type="number"
                        value={formData.pages}
                        onChange={(e) => setFormData({...formData, pages: e.target.value})}
                        placeholder="Number of pages"
                        min="1"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Book Copies
                      </label>
                      <Input
                        type="number"
                        value={formData.book_copies}
                        onChange={(e) => setFormData({...formData, book_copies: parseInt(e.target.value) || 1})}
                        placeholder="Number of copies"
                        min="1"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="available">Available</option>
                        <option value="borrowed">Borrowed</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Save className="h-4 w-4 mr-2" />
                      Add Book
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Book Modal */}
        <AnimatePresence>
          {showEditForm && selectedBook && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Edit Book</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowEditForm(false);
                      setSelectedBook(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <form onSubmit={handleEditBook} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Title *
                      </label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                        placeholder="Enter book title"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Author *
                      </label>
                      <Input
                        value={formData.author}
                        onChange={(e) => setFormData({...formData, author: e.target.value})}
                        required
                        placeholder="Enter author name"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        ISBN
                      </label>
                      <Input
                        value={formData.isbn}
                        onChange={(e) => setFormData({...formData, isbn: e.target.value})}
                        placeholder="e.g., 978-0134444321"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Publisher
                      </label>
                      <Input
                        value={formData.publisher}
                        onChange={(e) => setFormData({...formData, publisher: e.target.value})}
                        placeholder="e.g., Pearson Education"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Publication Year
                      </label>
                      <Input
                        type="number"
                        value={formData.publication_year}
                        onChange={(e) => setFormData({...formData, publication_year: e.target.value})}
                        placeholder="e.g., 2023"
                        min="1800"
                        max={new Date().getFullYear()}
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Category
                      </label>
                      <Input
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        placeholder="e.g., Programming, Web Development, Database"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Pages
                      </label>
                      <Input
                        type="number"
                        value={formData.pages}
                        onChange={(e) => setFormData({...formData, pages: e.target.value})}
                        placeholder="Number of pages"
                        min="1"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Book Copies
                      </label>
                      <Input
                        type="number"
                        value={formData.book_copies}
                        onChange={(e) => setFormData({...formData, book_copies: parseInt(e.target.value) || 1})}
                        placeholder="Number of copies"
                        min="1"
                        className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="available">Available</option>
                        <option value="borrowed">Borrowed</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Save className="h-4 w-4 mr-2" />
                      Update Book
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false);
                        setSelectedBook(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Confirm Deletion</h3>
              </div>
              <p className="text-slate-600 mb-2">
                Are you sure you want to delete this book?
              </p>
              <div className="bg-slate-50 rounded-lg p-3 mb-6">
                <p className="font-medium text-slate-900">{showDeleteConfirm.title}</p>
                <p className="text-sm text-slate-600">by {showDeleteConfirm.author}</p>
                <p className="text-xs text-slate-500">ISBN: {showDeleteConfirm.isbn}</p>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                This action cannot be undone. The book will be permanently removed from the system.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteBook(showDeleteConfirm.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Book
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message Toast */}
      <AnimatePresence>
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-[100] ${
              message.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <p>{message.text}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernBookManagement;
